import { DependencyContainer, InjectionToken, Lifecycle, container } from "@launchtray/tsyringe-async";
import { ICanvasScaler } from "../../canvas/ICanvasScaler";
import { SceneManager } from "scene-manager";
import { InjectableInterfaces } from "./InjectableInterfaces";
import { PreloaderScene } from "../scenes/preloader/PreloaderScene";
import { PreloaderController } from "../scenes/preloader/controllers/PreloaderController";
import { PreloaderModel } from "../scenes/preloader/models/PreloaderModel";
import { BundleResourcesPackage, IPackageRegistrator } from "resource-system";
import { EmptySquareModelFactory } from "../scenes/game/factories/squares/impl/EmptySquareModelFactory";
import { InjectableModelFactories } from "./InjectableModelFactories";
import { GameScene } from "../scenes/game/GameScene";
import { GameViewController } from "../scenes/game/controllers/GameViewController";
import { InjectableFactories } from "./InjectableFactories";
import { GameModel } from "../scenes/game/models/game/GameModel";
import { FigureModelFactory } from "../scenes/game/factories/figures/impl/FigureModelFactory";
import { HalfSquareModelFactory } from "../scenes/game/factories/squares/impl/HalfSquareModelFactory";
import { FullSquareModelFactory } from "../scenes/game/factories/squares/impl/FullSquareModelFactory";
import { TriangleModelFactory } from "../scenes/game/factories/triangles/impl/TriangleModelFactory";
import { TriangleModel } from "../scenes/game/models/triangle/impl/TriangleModel";
import { PlayerController } from "../scenes/game/controllers/PlayerController";
import { FullscreenBackground } from "../scenes/common/components/FullscreenBackground";
import { PlayerModel } from "../scenes/game/models/player/PlayerModel";
import { InfoScene } from "../scenes/info/InfoScene";
import { SettingsScene } from "../scenes/settings/SettingsScene";
import { SettingsController } from "../scenes/settings/controllers/SettingsController";
import { LoseScene } from "../scenes/lose/LoseScene";
import { LoseController } from "../scenes/lose/controller/LoseController";
import { Localization } from "../scenes/common/Localization";
import { RetryScene } from "../scenes/retry/RetryScene";
import { AudioManager } from "../audio/AudioManager";
import { DeckModel } from "../scenes/game/models/deck/DeckModel";
import { AdScene } from "../scenes/ad/AdScene";
import { InfoModel } from "../scenes/info/model/InfoModel";
import { InfoController } from "../scenes/info/controller/InfoController";
import { FilledCellModelFactory } from "../scenes/game/factories/cells/impl/FilledCellModelFactory";
import { IGameDistributionSdk } from "../gamedistribution/interfaces/IGameDistributionSdk";
import { DummySdkWrapper } from "../gamedistribution/DummySdkWrapper";
import { GameDistributionSdkWrapper } from "../gamedistribution/GameDistributionSdkWrapper";

export class BlockCutCleanerContext implements IPackageRegistrator {
    private internalContainer: DependencyContainer;

    public constructor() {
        this.internalContainer = container.createChildContainer();
    }

    public build(
        canvasScaler: ICanvasScaler,
        sceneManager: SceneManager
    ): void {

        const sdkImplementation = this.selectAdSdk(window.location.href);
        this.internalContainer.registerSingleton<IGameDistributionSdk>(InjectableInterfaces.IGameDistributionSdk, sdkImplementation);

        // this.internalContainer.register(InjectableModelFactories.ICellModelFactory, {
        //     useFactory: async (container: DependencyContainer) => new EmptyCellModelFactory(await container.resolve(EmptySquareModelFactory))
        // });
        this.internalContainer.register(InjectableModelFactories.ICellModelFactory, {
            useFactory: async (container: DependencyContainer) => new FilledCellModelFactory(
                await container.resolve(FullSquareModelFactory),
                await container.resolve(HalfSquareModelFactory)
            )
        });
        this.internalContainer.register(TriangleModel, {
            useClass: TriangleModel,
        });
        this.internalContainer.register(InjectableModelFactories.ITriangleModelFactory, {
            useFactory: async (container: DependencyContainer) => {
                return new TriangleModelFactory(TriangleModel);
            },
        });
        this.internalContainer.register(InjectableModelFactories.EmptySquareModelFactory, {
            useFactory: async (container: DependencyContainer) => {
                return new EmptySquareModelFactory()
            }
        });
        this.internalContainer.register(InjectableModelFactories.HalfSquareModelFactory, {
            useFactory: async (container: DependencyContainer) => {
                return new HalfSquareModelFactory(
                    await container.resolve(InjectableModelFactories.ITriangleModelFactory)
                );
            },
        });
        this.internalContainer.register(InjectableModelFactories.FullSquareModelFactory, {
            useFactory: async (container: DependencyContainer) => {
                return new FullSquareModelFactory(
                    await container.resolve(InjectableModelFactories.ITriangleModelFactory)
                );
            },
        });
        this.internalContainer.register(InjectableModelFactories.IFigureModelFactory, {
            useFactory: async (container) => {
                return new FigureModelFactory(
                    await container.resolve(InjectableModelFactories.EmptySquareModelFactory),
                    await container.resolve(InjectableModelFactories.HalfSquareModelFactory),
                    await container.resolve(InjectableModelFactories.FullSquareModelFactory)
                );
            }
        });

        this.internalContainer.registerInstance(InjectableInterfaces.ICanvasScaler, canvasScaler);
        this.internalContainer.registerInstance(SceneManager, sceneManager);
        this.internalContainer.registerInstance(InjectableInterfaces.IPackageRegistrator, this);
        this.internalContainer.register(PreloaderScene, PreloaderScene);
        this.internalContainer.register(PreloaderController, PreloaderController, { lifecycle: Lifecycle.ResolutionScoped });
        this.internalContainer.register(PreloaderModel, PreloaderModel, { lifecycle: Lifecycle.ResolutionScoped });

        // GameScene
        this.internalContainer.registerSingleton(GameModel, GameModel);
        this.internalContainer.registerSingleton(PlayerModel, PlayerModel);
        this.internalContainer.registerSingleton(DeckModel, DeckModel);
        this.internalContainer.register(PlayerController, PlayerController);
        this.internalContainer.register(GameViewController, GameViewController);
        this.internalContainer.register(GameScene, GameScene);
        this.internalContainer.register(InjectableFactories.GameSceneFactory, {
            useValue: async () => {
                return await this.internalContainer.resolve(GameScene);
            }
        });

        // InfoScene
        this.internalContainer.register(InfoScene, InfoScene);
        this.internalContainer.registerSingleton(InfoModel, InfoModel);
        this.internalContainer.register(InfoController, InfoController);
        this.internalContainer.register(InjectableFactories.InfoSceneFactory, {
            useValue: async () => {
                return await this.internalContainer.resolve(InfoScene);
            }
        });

        // SettingsScene
        this.internalContainer.register(SettingsScene, SettingsScene);
        this.internalContainer.register(SettingsController, SettingsController);
        this.internalContainer.register(InjectableFactories.SettingsSceneFactory, {
            useValue: async () => {
                return await this.internalContainer.resolve(SettingsScene);
            }
        });

        // LoseScene
        this.internalContainer.register(LoseScene, LoseScene);
        this.internalContainer.register(LoseController, LoseController);
        this.internalContainer.register(InjectableFactories.LoseSceneFactory, {
            useValue: async () => {
                return await this.internalContainer.resolve(LoseScene);
            }
        });

        // Ad Scene
        this.internalContainer.register(AdScene, AdScene);
        this, this.internalContainer.register(InjectableFactories.AdSceneFactory, {
            useValue: async () => {
                return await this.internalContainer.resolve(AdScene);
            }
        });

        // Restart
        this.internalContainer.register(RetryScene, RetryScene);
        this.internalContainer.register(InjectableFactories.RetrySceneFactory, {
            useValue: async () => {
                return await this.internalContainer.resolve(RetryScene);
            }
        });
        
        // Common
        this.internalContainer.registerSingleton(FullscreenBackground, FullscreenBackground);
        this.internalContainer.registerSingleton(Localization, Localization);
        this.internalContainer.registerSingleton(AudioManager, AudioManager);
    }

    private async resolveWithParams<T>(
        container: DependencyContainer,
        token: InjectionToken<T>,
        ...args: Array<ParamDescription<any>>
    ): Promise<T> {
        const tempContainer = container.createChildContainer();
        for (const arg of args) {
            tempContainer.registerInstance(arg.token, arg.instance);
        }
        const instance = await tempContainer.resolve(token);
        tempContainer.clearInstances();
        tempContainer.reset();
        return instance;
    }

    public async resolve<T>(token: InjectionToken<T>): Promise<T> {
        return await this.internalContainer.resolve(token);
    }

    public registerPackage(pkgType: new () => BundleResourcesPackage, pkg: BundleResourcesPackage): void {
        this.internalContainer.registerInstance(pkgType, pkg);
    }

    private selectAdSdk(host: string): new (...args: any[]) => IGameDistributionSdk {
        if (host.includes("localhost") || host.includes("127.0.0.1") || host.includes("gamefactory.by")) {
            return DummySdkWrapper;
        }
        return GameDistributionSdkWrapper;
    }
}

class ParamDescription<T> {
    public readonly token: InjectionToken<T>;
    public readonly instance: T;

    public constructor(token: InjectionToken<T>, instance: T) {
        this.token = token;
        this.instance = instance;
    }
}